import { arrivalClock } from '../components/ArrivalInput';
import { useEffect, useMemo, useState } from 'react';
import { kindLabel } from '../lib/driverKind';
import { api, type CarAlloc, type ListResult } from '../lib/api';
import { DataTable, ErrorBox, dash, money, useList, wallClock, type Column, type Row } from '../components/DataTable';
import { canScreen, useMe } from '../lib/me';
import ViaEditor from './ViaEditor';
import Modal from '../components/Modal';
import ExternalAllocForm from '../components/ExternalAllocForm';
import { CarYearSelect, useCarKinds } from '../components/CarKindPicker';
import { BIDDING_TYPES } from '../components/RecvFields';
import { DISPT_STATUS_TONE, disptStatusLabel } from '../lib/disptStatus';
import { useAutoSearch } from '../lib/search';

const COLUMNS: Column[] = [
  // phone: 폰에선 차량번호를 왼쪽에 고정하고 차종·기사구분·소속은 뺀다(2026-09-30 way).
  { key: 'car_no', label: '차량번호', phone: 'pin' },
  { key: 'car_alloc_id', label: '편성 NO', phone: 'hide' },
  { key: 'car_name', label: '차종', phone: 'hide' },
  { key: 'driver_name', label: '기사' },
  { key: 'driver_phone', label: '기사 연락처' }, // #353
  { key: 'driver_kind', label: '기사구분', phone: 'hide', render: (r) => kindLabel(r.driver_kind) },
  { key: 'owner_name', label: '차주' },
  { key: 'grp_name', label: '소속', phone: 'hide' },
  { key: 'start_time', label: '시작', render: (r) => wallClock(r.start_time) },
  { key: 'end_time', label: '종료', render: (r) => wallClock(r.end_time) },
  { key: 'change_cost', label: '변경단가', align: 'right', render: (r) => money(r.change_cost) },
];

/**
 * 노선 상세 — 그 노선에 어느 차량·기사가 **배차**됐는지.
 *
 * 배정 건수가 곧 상태를 정한다: 배정 수 ≥ 필요 대수면 '배차완료'(레거시 View_DisptStatus).
 * 그래서 목록의 "배정/필요"와 여기 행 수는 같아야 한다 — 다르면 둘 중 하나가 잘못된 것이다.
 */
export default function DisptDetail({
  dispt,
  onClose,
  onChanged,
  onEdit,
  onCopy,
  onAdd,
  onEditRecv,
  onDeleted,
}: {
  dispt: Row;
  onClose: () => void;
  onChanged?: (msg: string) => void;
  onEdit?: () => void;
  /** 노선 복사(#178) — 이 노선 값을 채운 새 노선 등록 창. */
  onCopy?: () => void;
  /** 같은 접수에 빈 새 노선 등록 창(#293). */
  onAdd?: () => void;
  /** 이 노선의 접수 수정 창(2026-10-19 way 「노선 탭에서 작업하다 접수 탭으로 다시 가야 하는 불편」). */
  onEditRecv?: () => void;
  /** 노선을 지운 뒤(#109). 부모가 상세를 닫고 목록을 다시 읽는다. */
  onDeleted?: (msg: string) => void;
}) {
  const disptId = Number(dispt.dispt_id);
  const { me } = useMe();
  const canWrite = canScreen(me, 'dispatch');
  const [nonce, setNonce] = useState(0);
  const [busy, setBusy] = useState(false);
  const [actError, setActError] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const [extOpen, setExtOpen] = useState(false);
  const [cancelTarget, setCancelTarget] = useState<number | null>(null);
  // 필요 대수(#293) — 받은 Row 는 그 순간 사본이라 −/+ 뒤 값은 여기 둔다. 노선이 바뀌면 다시 받는다.
  const [carCnt, setCarCnt] = useState<number>(Number(dispt.car_cnt ?? 1));
  useEffect(() => setCarCnt(Number(dispt.car_cnt ?? 1)), [disptId, dispt.car_cnt]);

  // 입찰·연식(#322·#323) — 받은 Row 는 그 순간 사본이라 저장 뒤 값은 여기 둔다.
  const bidInit = () => ({
    isBidding: dispt.is_bidding === true,
    biddingType: String(dispt.bidding_type ?? ''),
    closeDate: String(dispt.close_date ?? '').slice(0, 10),
    carYear: Number(dispt.car_year ?? 0) || undefined,
    status: String(dispt.dispt_status ?? ''),
    // 받는 인승(10-20) — 노선에서 고른 것. 비면 차종 설정(kindSeats)대로.
    bidSeats: Array.isArray(dispt.bid_seats) ? (dispt.bid_seats as number[]) : ([] as number[]),
    // #413 접수는 공개여도 이 노선만 입찰에서 뺐는지.
    bidOff: dispt.bid_off === true,
  });
  const [bid, setBid] = useState(bidInit);
  const [bidOpen, setBidOpen] = useState(false);
  const [bidDraft, setBidDraft] = useState(bidInit);
  // #413 노선별 입찰 — 같은 접수 노선 목록(창을 열 때 읽는다) · 고른 것 = 입찰에 넣을 노선.
  const [bidRoutes, setBidRoutes] = useState<{ id: number; name: string; start: string; status: string; off: boolean }[] | null>(null);
  const [bidPick, setBidPick] = useState<Set<number>>(new Set());
  useEffect(() => {
    if (!bidOpen) return;
    let cancelled = false;
    setBidRoutes(null);
    api.listRecvDispts({ recvId: Number(dispt.recv_id) })
      .then((r) => {
        if (cancelled) return;
        const rows = r.rows.map((x) => ({
          id: Number(x.dispt_id), name: String(x.dispt_name ?? ''), start: String(x.start_time ?? ''),
          status: String(x.dispt_status ?? ''), off: x.bid_off === true,
        }));
        setBidRoutes(rows);
        // 처음 공개하는 접수면 이 노선만 · 이미 공개면 지금 입찰 중인(빼지 않은) 노선.
        setBidPick(new Set(bid.isBidding ? rows.filter((x) => !x.off).map((x) => x.id) : [disptId]));
      })
      .catch((e) => !cancelled && setActError(e instanceof Error ? e.message : String(e)));
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bidOpen, disptId]);
  const kindSeats: number[] = Array.isArray(dispt.kind_seats) ? (dispt.kind_seats as number[]) : [];
  const carKinds = useCarKinds();
  // 고를 수 있는 인승 = 차종 설정에 나오는 인승 전부 + 지금 값.
  const seatChoices = useMemo(() => {
    const all = new Set<number>([...kindSeats, ...bid.bidSeats]);
    for (const k of carKinds ?? []) for (const n of k.seats ?? []) all.add(Number(n));
    return [...all].filter((n) => n > 0).sort((a, b) => a - b);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carKinds, disptId]);
  useEffect(() => {
    setBid(bidInit());
    setBidOpen(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disptId]);

  async function saveBid() {
    setBusy(true);
    setActError(null);
    try {
      const r = await api.setDisptBidding({
        disptId,
        isBidding: bidDraft.isBidding,
        biddingType: bidDraft.isBidding ? bidDraft.biddingType || undefined : undefined,
        closeDate: bidDraft.closeDate || undefined,
        carYear: bidDraft.carYear ?? 0,
        // 차종 설정과 같으면 비워 둔다(설정을 바꾸면 따라가게).
        bidSeats: sameSet(bidDraft.bidSeats, kindSeats) ? [] : bidDraft.bidSeats,
        // #413 노선이 둘 이상일 때만 고른다(하나면 늘 그 노선).
        bidRoutes: bidDraft.isBidding && bidRoutes && bidRoutes.length > 1 ? [...bidPick] : undefined,
      });
      setBid({
        isBidding: r.isBidding,
        biddingType: r.biddingType ?? '',
        closeDate: r.closeDate ?? '',
        carYear: r.carYear || undefined,
        status: r.dispt_status ?? '',
        bidSeats: r.bidSeats ?? [],
        bidOff: r.bidOff === true,
      });
      setBidOpen(false);
      onChanged?.(r.isBidding ? '기사에게 공개(입찰)로 저장했습니다.' : '비공개로 저장했습니다.');
    } catch (e) {
      setActError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function changeCarCnt(next: number) {
    if (next < 1 || next === carCnt) return;
    setBusy(true);
    setActError(null);
    try {
      const r = await api.setDisptCarCnt({ disptId, carCnt: next });
      setCarCnt(r.carCnt);
      onChanged?.(`필요 대수를 ${r.carCnt}대로 바꿨습니다.`);
    } catch (e) {
      setActError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const { rows, loading, error } = useList(
    () => api.listDisptCarSchs({ disptId, limit: 200 }) as unknown as Promise<ListResult<Row>>,
    [disptId, nonce],
  );

  async function act(run: () => Promise<unknown>, msg: string) {
    setBusy(true);
    setActError(null);
    try {
      await run();
      setNonce((n) => n + 1);
      onChanged?.(msg);
    } catch (e) {
      setActError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  // 차량 배정 — 시간이 겹치면 막지 않고 확인받는다(#299·#311). 서버가 겹친 운행을 문구에 담아
  // details.overlap 으로 거부하면 그 목록을 그대로 보여 주고, 확인하면 allowOverlap 으로 다시 부른다.
  async function assign(carAllocId: number) {
    await assignMany([{ car_alloc_id: carAllocId }]);
  }

  /** 한 번 = 한 대씩 순서대로(#373 여러 명 고르기). 겹침 확인·실패는 그 건만 — 나머지는 이어서 넣는다. */
  async function assignMany(list: { car_alloc_id: number; car_no?: string | null; driver_name?: string | null }[]) {
    setBusy(true);
    setActError(null);
    let done = 0;
    const errs: string[] = [];
    try {
      for (const a of list) {
        const who = list.length > 1 ? `${a.car_no ?? ''} ${a.driver_name ?? ''}`.trim() || `편성 ${a.car_alloc_id}` : '';
        try {
          try {
            await api.assignCar({ disptId, carAllocId: a.car_alloc_id });
          } catch (e) {
            if ((e as { details?: { overlap?: boolean } })?.details?.overlap !== true) throw e;
            if (!window.confirm([who && `[${who}]`, (e as Error).message, '', '그래도 배정할까요?'].filter(Boolean).join('\n'))) continue;
            await api.assignCar({ disptId, carAllocId: a.car_alloc_id, allowOverlap: true });
          }
          done++;
        } catch (e) {
          const m = e instanceof Error ? e.message : String(e);
          errs.push(who ? `${who}: ${m}` : m);
        }
      }
      if (done > 0) {
        setNonce((n) => n + 1);
        onChanged?.(done > 1 ? `차량 ${done}대를 배정했습니다.` : '차량을 배정했습니다.');
      }
      if (errs.length > 0) setActError(errs.join('\n'));
      else if (done > 0) setPicking(false);
    } finally {
      setBusy(false);
    }
  }

  // 노선 삭제(#109) — 배정·정산·입찰이 남았거나 마감된 달이면 서버가 사유와 함께 거부한다.
  async function removeDispt() {
    if (!window.confirm(`노선 ${disptId}번 「${String(dispt.dispt_name ?? '')}」을(를) 삭제할까요?`)) return;
    setBusy(true);
    setActError(null);
    try {
      await api.deleteDispt({ disptId });
      onDeleted?.(`노선 ${disptId}번을 삭제했습니다.`);
    } catch (e) {
      setActError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const columns: Column[] = [
    ...COLUMNS,
    ...(canWrite
      ? [
          {
            key: '_act',
            label: '',
            render: (r: Row) => (
              // 11-03 way: 해제·취소 두 버튼이 혼선 → 「배정 빼기」 하나 + 이유 고르기(RemoveBox).
              //   서버는 그대로 — 입력 실수 = unassignCar · 취소 = cancelAssignment(이력·수수료).
              <button
                className="btn-ghost py-1 text-xs"
                disabled={busy}
                title="이 배정을 노선에서 뺍니다. 누르면 이유(입력 실수 / 회사·거래처 사정 취소 / 기사 사정 취소)를 고릅니다."
                onClick={() => setCancelTarget(Number(r.dispt_car_sch_id))}
              >
                배정 빼기
              </button>
            ),
          } satisfies Column,
        ]
      : []),
  ];

  return (
    <div className="card space-y-2 p-3">
      {/* 촘촘하게(10-15 way 「위가 너무 넓어 아래 목록이 한눈에 안 보인다」) — 여백·버튼을 줄이고 경유지·배정 머리를 한 줄로. */}
      <div className="flex items-start gap-3">
        <div className="min-w-0 flex-1">
          {/* 머리 한 줄(#274 윤일향 「상단 정보는 한 줄로 · 너무 넓음」) + 접수·노선 번호(#277). 좁으면 줄바꿈. */}
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm">
            <span className="font-bold">{dash(dispt.cust_name)} · {dash(dispt.dispt_name)}</span>
            <span className="rounded bg-canvas px-1.5 py-0.5 text-xs text-ink-soft">접수 NO {dash(dispt.recv_id)}</span>
            <span className="rounded bg-canvas px-1.5 py-0.5 text-xs text-ink-soft">노선 NO {dash(dispt.dispt_id)}</span>
            <span><span className="text-ink-soft">출발</span> {wallClock(dispt.start_time)} · {dash(dispt.start_loc)}</span>
            <span><span className="text-ink-soft">도착</span> {arrivalClock(dispt.end_time)} · {dash(dispt.end_loc)}</span>
            <span className="inline-flex items-baseline gap-1">
              <span className="text-ink-soft">차종·대수</span> {dash(dispt.car_kind)} · {dash(dispt.assigned_cnt)}/{carCnt}대
              {/* #293 필요 대수 바로 조정 — 배정 수 아래로는 서버가 막는다. */}
              {canWrite && (
                <span className="inline-flex items-center gap-0.5 self-center">
                  <button className="btn-ghost px-1.5 py-0 text-xs" disabled={busy || carCnt <= 1}
                    title="필요 대수 1대 줄이기" onClick={() => changeCarCnt(carCnt - 1)}>−</button>
                  <button className="btn-ghost px-1.5 py-0 text-xs" disabled={busy}
                    title="필요 대수 1대 늘리기" onClick={() => changeCarCnt(carCnt + 1)}>+</button>
                </span>
              )}
              ({dash(dispt.trip_type)})
            </span>
            <span><span className="text-ink-soft">청구·운행료</span> {money(dispt.cost_cust)} / {money(dispt.cost_driver)}</span>
            {/* #323 입찰 표기 — 공개(진한 파랑)·비공개(회색) · 연식 제한 · 「입찰·연식」 한 곳에서 고치기 */}
            <span className="inline-flex items-center gap-1.5">
              <span className={`rounded px-1.5 py-0.5 text-xs font-medium ${DISPT_STATUS_TONE[bid.status] ?? 'bg-canvas text-ink-soft'}`}>
                {bid.status ? disptStatusLabel(bid.status) : '—'}
              </span>
              <span className={`rounded px-1.5 py-0.5 text-xs ${bid.isBidding ? 'bg-blue-50 text-blue-800' : 'bg-slate-100 text-slate-600'}`}>
                {bid.isBidding && bid.bidOff
                  ? '이 노선은 입찰 안 함'
                  : bid.isBidding
                  ? `기사에게 공개${bid.biddingType ? ` · ${bid.biddingType}` : ''}${bid.closeDate ? ` · 마감 ${bid.closeDate}` : ''}`
                  : '비공개'}
              </span>
              <span className="text-xs text-ink-soft">연식 {bid.carYear ? `${bid.carYear}년 이내` : '제한 없음'}</span>
              <span className="text-xs text-ink-soft">
                인승 {(bid.bidSeats.length ? bid.bidSeats : kindSeats).length ? (bid.bidSeats.length ? bid.bidSeats : kindSeats).join('·') : '제한 없음'}
              </span>
              {/* #364 입찰 알림 — 공개되면 조건 맞는 기사 폰에 한 번(bidPush.ts 5분 주기). 보낸 기록이 있으면 시각·대수. */}
              {(() => {
                const p = dispt.bid_push as { at?: string; drivers?: number; ok?: number; fail?: number } | null | undefined;
                if (p?.at) {
                  return (
                    <span className="rounded bg-emerald-50 px-1.5 py-0.5 text-xs text-emerald-800"
                      title={`보낸 폰 ${p.ok ?? 0}대 성공${p.fail ? ` · ${p.fail}대 실패` : ''}`}>
                      입찰 알림 {p.at} · 기사 {p.drivers ?? 0}명
                    </span>
                  );
                }
                return bid.isBidding && bid.status === '입찰'
                  ? <span className="text-xs text-ink-soft">입찰 알림 보내기 전</span>
                  : null;
              })()}
              {canWrite && (
                <button className="rounded border border-blue-600 bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-800 hover:bg-blue-100" disabled={busy}
                  onClick={() => { setBidDraft(bid); setBidOpen((o) => !o); }}>
                  입찰 공개·연식
                </button>
              )}
            </span>
          </div>
          {/* #390 입력은 팝업으로 — 상세 안에 펼치면 버튼 글자만 바뀐 것처럼 보였다. */}
          {bidOpen && canWrite && (
            <Modal width="max-w-3xl">
            <InputPopup title="입찰 공개·연식" sub={`노선 ${disptId} · ${dash(dispt.dispt_name)}`} error={actError}>
            <div className="flex flex-wrap items-end gap-3 text-sm">
              <div role="radiogroup" aria-label="배차 입찰" className="flex gap-1.5">
                {[
                  { v: false, t: '비공개' },
                  { v: true, t: '기사에게 공개' },
                ].map((o) => (
                  <label key={String(o.v)}
                    className={`flex cursor-pointer select-none items-center gap-1.5 rounded border px-2 py-1 ${
                      bidDraft.isBidding === o.v ? 'border-blue-600 bg-white font-medium' : 'border-line bg-white/60'
                    }`}>
                    <input type="radio" name={`bid-${disptId}`} checked={bidDraft.isBidding === o.v}
                      onChange={() => setBidDraft((d) => ({ ...d, isBidding: o.v }))} />
                    {o.t}
                  </label>
                ))}
              </div>
              {bidDraft.isBidding && (
                <>
                  <label className="flex flex-col gap-0.5">
                    <span className="text-xs text-ink-soft">입찰 유형</span>
                    <select className="field py-1" value={bidDraft.biddingType}
                      onChange={(e) => setBidDraft((d) => ({ ...d, biddingType: e.target.value }))}>
                      <option value="">선택 안 함</option>
                      {BIDDING_TYPES.map((b) => <option key={b} value={b}>{b}</option>)}
                      {bidDraft.biddingType && !BIDDING_TYPES.includes(bidDraft.biddingType) && (
                        <option value={bidDraft.biddingType}>{bidDraft.biddingType}</option>
                      )}
                    </select>
                  </label>
                  <label className="flex flex-col gap-0.5">
                    <span className="text-xs text-ink-soft">입찰 마감일</span>
                    <input type="date" className="field py-1" value={bidDraft.closeDate}
                      onChange={(e) => setBidDraft((d) => ({ ...d, closeDate: e.target.value }))} />
                  </label>
                  {/* #413 노선별 입찰 — 접수 노선이 둘 이상이면 입찰할 노선만 고른다. */}
                  {bidRoutes && bidRoutes.length > 1 && (
                    <div className="basis-full">
                      <div className="mb-1 flex items-center gap-2 text-xs text-ink-soft">
                        <span>입찰할 노선({bidPick.size}/{bidRoutes.length})</span>
                        <button type="button" className="btn-ghost px-1.5 py-0 text-xs"
                          onClick={() => setBidPick(new Set(bidRoutes.map((x) => x.id)))}>전부</button>
                        <button type="button" className="btn-ghost px-1.5 py-0 text-xs"
                          onClick={() => setBidPick(new Set([disptId]))}>이 노선만</button>
                      </div>
                      <div className="max-h-48 overflow-auto rounded border border-line bg-white">
                        {bidRoutes.map((x) => (
                          <label key={x.id} className="flex cursor-pointer items-center gap-2 border-b border-line px-2 py-1 text-xs last:border-b-0">
                            <input type="checkbox" checked={bidPick.has(x.id)}
                              onChange={() => setBidPick((s) => {
                                const n = new Set(s);
                                if (n.has(x.id)) n.delete(x.id); else n.add(x.id);
                                return n;
                              })} />
                            <span className="text-ink-faint">{x.id}</span>
                            <span className={x.id === disptId ? 'font-medium' : ''}>{x.name || '—'}</span>
                            <span className="text-ink-faint">{x.start}</span>
                            {x.status === '배차완료' && <span className="text-ink-faint">· 배차완료</span>}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
              <div className="flex flex-col gap-0.5">
                <span className="text-xs text-ink-soft">
                  받는 인승(이 노선) — 누르면 넣고 빼기 {sameSet(bidDraft.bidSeats.length ? bidDraft.bidSeats : kindSeats, kindSeats) ? '· 차종 기본' : ''}
                </span>
                <div className="flex flex-wrap gap-1">
                  {seatChoices.map((n) => {
                    const cur = bidDraft.bidSeats.length ? bidDraft.bidSeats : kindSeats;
                    const on = cur.includes(n);
                    return (
                      <button key={n} type="button"
                        className={`rounded border px-1.5 py-0.5 text-xs ${on ? 'border-blue-600 bg-blue-600 text-white' : 'border-line bg-white text-ink-soft'}`}
                        onClick={() => setBidDraft((d) => {
                          const base = d.bidSeats.length ? d.bidSeats : kindSeats;
                          const next = on ? base.filter((x) => x !== n) : [...base, n].sort((a, b) => a - b);
                          return { ...d, bidSeats: next };
                        })}>
                        {n}
                      </button>
                    );
                  })}
                  {seatChoices.length === 0 && <span className="text-xs text-ink-faint">차종 설정에 인승이 없습니다</span>}
                </div>
              </div>
              <label className="flex flex-col gap-0.5">
                <span className="text-xs text-ink-soft">연식 제한(이 노선)</span>
                <CarYearSelect className="field py-1" value={bidDraft.carYear}
                  onChange={(v) => setBidDraft((d) => ({ ...d, carYear: v }))} />
              </label>
              <button className="btn-primary py-1 text-xs"
                disabled={busy || (bidDraft.isBidding && (!bidDraft.closeDate || (bidRoutes !== null && bidRoutes.length > 1 && bidPick.size === 0)))}
                onClick={saveBid}>
                저장
              </button>
              <button className="btn-ghost py-1 text-xs" disabled={busy} onClick={() => setBidOpen(false)}>취소</button>
              <span className="basis-full text-xs text-ink-soft">
                공개·유형·마감일은 이 접수의 노선 전부에 걸리고 그중 고른 노선만 기사앱에 뜹니다, 인승·연식은 이 노선에만 걸립니다 · 기사앱에는 고른 인승이면서 연식 안에 드는 차량(인승·연식이 등록된 차량)을 가진 기사님께만 보이고, 그 차량으로만 신청됩니다 · 인승을 다 빼면 차종 기본으로 돌아갑니다(인승 제한을 없애려면 차종을 「차종 무관」으로)
              </span>
            </div>
            </InputPopup>
            </Modal>
          )}
          {/* #275 노선 참고사항 */}
          {dispt.request ? (
            <div className="mt-1 whitespace-pre-line break-keep rounded bg-amber-50 px-2 py-1 text-sm text-amber-900">
              <span className="font-semibold">참고사항</span> {String(dispt.request)}
            </div>
          ) : null}
        </div>
        <div className="flex shrink-0 gap-1.5">
          {canWrite && onEditRecv && (
            <button className="btn-ghost py-1 text-xs" onClick={onEditRecv} title="이 노선의 접수(거래처·운행 기간·청구·배차 소속·입찰 등)를 여기서 고칩니다">
              접수 수정
            </button>
          )}
          {canWrite && onAdd && (
            <button className="btn-ghost py-1 text-xs" onClick={onAdd} title="이 접수에 새 노선을 등록합니다">
              노선 추가
            </button>
          )}
          {canWrite && onEdit && (
            <button className="btn-ghost py-1 text-xs" onClick={onEdit}>
              노선 수정
            </button>
          )}
          {canWrite && onCopy && (
            <button className="btn-ghost py-1 text-xs" onClick={onCopy} title="이 노선 값을 채운 채 같은 접수에 새 노선을 등록합니다">
              노선 복사
            </button>
          )}
          {canWrite && onDeleted && (
            <button className="btn-ghost py-1 text-xs text-red-600" onClick={removeDispt} disabled={busy}>
              노선 삭제
            </button>
          )}
          <button className="btn-ghost py-1 text-xs" onClick={onClose}>
            닫기
          </button>
        </div>
      </div>

      {error && <ErrorBox message={error} />}
      {actError && <ErrorBox message={actError} />}

      <div>
        <div className="mb-2 flex flex-wrap items-start gap-x-8 gap-y-2">
          <ViaEditor disptId={disptId} readOnly={!canWrite} onSaved={(m) => onChanged?.(m)} />
          <div className="flex items-center gap-3">
            <span className="text-sm font-semibold text-ink-soft">배정 차량 {loading ? '' : `${rows.length}대`}</span>
            {canWrite && !picking && (
              <button className="btn-ghost py-1 text-xs" onClick={() => setPicking(true)} disabled={busy}>
                차량 배정
              </button>
            )}
            {/* #348 외부차량·예비기사(스페어) — 외부 편성 양식으로 만들고 이 노선에 바로 배정 */}
            {canWrite && (
              <button className="btn-ghost py-1 text-xs" onClick={() => setExtOpen(true)} disabled={busy}
                title="외부 차량(개인·관광사)이나 예비기사를 외부 편성으로 등록하고 이 노선에 바로 배정합니다">
                외부·예비 기사 등록
              </button>
            )}
          </div>
        </div>

        {cancelTarget !== null && (
          <Modal width="max-w-2xl">
          <InputPopup title="배정 빼기" sub={`노선 ${disptId} · ${dash(dispt.dispt_name)}`} error={actError}>
          <RemoveBox
            busy={busy}
            costDriver={dispt.cost_driver}
            onCancel={() => setCancelTarget(null)}
            onConfirm={(reason, comment) =>
              reason === 'mistake'
                ? act(async () => {
                    const res = await api.unassignCar({ disptCarSchId: cancelTarget });
                    setCancelTarget(null);
                    return res;
                  }, '배정을 뺐습니다.')
                : act(async () => {
                    const res = await api.cancelAssignment({
                      disptCarSchId: cancelTarget,
                      isDriverCancel: reason === 'driver',
                      comment,
                    });
                    setCancelTarget(null);
                    return res;
                  }, '배차를 취소했습니다. 취소 이력이 기록되었습니다.')
            }
          />
          </InputPopup>
          </Modal>
        )}

        {picking && (
          <Modal width="max-w-4xl">
          <InputPopup title="차량 배정" sub={`노선 ${disptId} · ${dash(dispt.dispt_name)} · 배정 ${rows.length}/${carCnt}대`} error={actError}>
          <AllocPicker
            busy={busy}
            onDate={dispt.recv_type === '일반' ? String(dispt.start_time ?? '').slice(0, 10) || undefined : undefined}
            onCancel={() => setPicking(false)}
            onPick={(a) => assign(a.car_alloc_id)}
            onPickMany={(list) => assignMany(list)}
          />
          </InputPopup>
          </Modal>
        )}

        <DataTable columns={columns} rows={rows} loading={loading} tableId="dispatch.carsch" />
      </div>

      {extOpen && (
        <Modal width="max-w-4xl">
          <ExternalAllocForm
            onClose={() => setExtOpen(false)}
            onSaved={(msg, carAllocId) => {
              setExtOpen(false);
              onChanged?.(msg);
              if (carAllocId) void assign(carAllocId);
            }}
          />
        </Modal>
      )}
    </div>
  );
}

type RemoveReason = 'mistake' | 'company' | 'driver';

/**
 * 배정 빼기 — 이유를 고른다(11-03 way · 옛 「배정 해제」·「배차 취소」 두 버튼을 합침).
 *
 * 노선·배정·정산이 빠지는 것은 셋 다 같고, 다른 것은 **기록과 수수료**뿐이다.
 *   입력 실수 = unassignCar(기록 없음) · 회사·거래처 사정 = 관리자 취소(기록 · 수수료 0%)
 *   기사 사정 = 기사 취소(기록 · 운행료 10% 를 그 달 명세에서 공제 — salaryGen 「취소공제」).
 * 기본은 입력 실수 — 잘못 눌러도 기사에게 공제가 생기지 않는 쪽.
 * 요율은 서버 setting(CANCEL_FEE_RATE·_ADMIN)이 정본이고 화면 표기는 그 현재값(10·0)을 적어 둔 것.
 */
function RemoveBox({
  busy,
  costDriver,
  onConfirm,
  onCancel,
}: {
  busy: boolean;
  costDriver: unknown;
  onConfirm: (reason: RemoveReason, comment: string) => void;
  onCancel: () => void;
}) {
  const [reason, setReason] = useState<RemoveReason>('mistake');
  const [comment, setComment] = useState('');
  const cost = Number(costDriver);
  const fee = Number.isFinite(cost) && cost > 0 ? Math.round(cost * 0.1) : null;
  const opts: { v: RemoveReason; label: string; note: string }[] = [
    { v: 'mistake', label: '입력 실수', note: '잘못 넣은 기사·차량을 뺍니다. 기록이 남지 않습니다.' },
    { v: 'company', label: '회사·거래처 사정으로 취소', note: '취소 기록이 남습니다. 수수료 없음.' },
    {
      v: 'driver',
      label: '기사 사정으로 취소',
      note: `취소 기록이 남고 운행료의 10%${fee !== null ? `(약 ${fee.toLocaleString()}원)` : ''}가 그 기사님 이번 달 명세서에서 공제됩니다.`,
    },
  ];

  return (
    <div className="rounded-lg border border-line p-3">
      <p className="text-xs text-ink-soft">
        어떤 이유든 노선은 그대로 남고, 이 기사·차량만 빠집니다(운행료 정산에서도 빠집니다).
      </p>
      <div className="mt-2 space-y-1.5 text-sm">
        {opts.map((o) => (
          <label key={o.v} className="flex cursor-pointer items-start gap-2">
            <input type="radio" className="mt-1" checked={reason === o.v} onChange={() => setReason(o.v)} />
            <span>
              {o.label}
              <span className={`block text-xs ${o.v === 'driver' ? 'text-amber-700' : 'text-ink-faint'}`}>{o.note}</span>
            </span>
          </label>
        ))}
      </div>
      <div className="mt-2 flex gap-2">
        {reason !== 'mistake' ? (
          <input
            className="field flex-1"
            placeholder="취소 사유 (선택)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
        ) : (
          <span className="flex-1" />
        )}
        <button className="btn-primary" disabled={busy} onClick={() => onConfirm(reason, comment)}>
          {reason === 'mistake' ? '배정 빼기' : '취소 확정'}
        </button>
        <button className="btn-ghost" disabled={busy} onClick={onCancel}>
          닫기
        </button>
      </div>
    </div>
  );
}

/**
 * 배정할 편성 고르기.
 *
 * 편성은 **차량 × 차주 × 소속 × 기사** 묶음이고 배차·정산의 앵커다. 차량이나 기사를 따로 고르지 않는다.
 * "정산 진행중" 편성만 기본으로 보여준다 — 종료된 편성을 배정하면 정산이 따라오지 않는다.
 */
function AllocPicker({
  busy,
  onDate,
  onPick,
  onPickMany,
  onCancel,
}: {
  busy: boolean;
  /** 일반 노선 출발일 — 그 날 운행하는 편성만 보인다(서버 배정 가드와 같은 규칙 · 통근은 안 거름). */
  onDate?: string;
  onPick: (a: CarAlloc) => void;
  /** #373 네모로 여러 명 고른 뒤 한 번에 배정. */
  onPickMany: (list: CarAlloc[]) => void;
  onCancel: () => void;
}) {
  const [term, setTerm] = useState('');
  const [hits, setHits] = useState<CarAlloc[]>([]);
  const [searching, setSearching] = useState(false);
  const [unfolded, setUnfolded] = useState<Set<string>>(new Set());
  // 고른 편성 — 검색어를 바꿔도 남는다(이름을 하나씩 찾아 고르므로).
  const [picked, setPicked] = useState<Map<number, CarAlloc>>(new Map());
  const toggle = (a: CarAlloc) =>
    setPicked((m) => {
      const n = new Map(m);
      if (n.has(a.car_alloc_id)) n.delete(a.car_alloc_id);
      else n.set(a.car_alloc_id, a);
      return n;
    });
  // #356 기사님별 묶음 — 묶음 순서는 받은 순서(첫 등장) · 묶음 안은 최근 편성(번호 큰 것) 먼저. 기사 없는 편성은 각자 한 줄.
  const groups = useMemo(() => {
    const m = new Map<string, CarAlloc[]>();
    for (const a of hits) {
      const k = a.driver_id != null ? `d${a.driver_id}` : `a${a.car_alloc_id}`;
      const list = m.get(k) ?? [];
      list.push(a);
      m.set(k, list);
    }
    return [...m].map(([key, list]) => ({ key, list: [...list].sort((x, y) => Number(y.car_alloc_id) - Number(x.car_alloc_id)) }));
  }, [hits]);

  /** 실제 조회. 버튼·Enter 와 자동검색이 **같은 경로**를 쓴다(둘이 갈리면 한쪽만 고쳐진다). */
  async function run(raw: string) {
    setSearching(true);
    try {
      // 기사님 한 줄로 묶으므로(#356) 넉넉히 받는다 — 동영투어 순환 기사는 한 사람이 편성 5개다.
      // #385 이름을 띄어 쓰거나 쉼표로 나열하면(「김갑식 김영수」) 낱말마다 찾아 합친다 — 친 순서대로 · 같은 편성은 한 번.
      const words = raw.trim().split(/[\s,]+/).filter(Boolean);
      const lists = await Promise.all((words.length > 1 ? words : [raw.trim()]).map((w) =>
        api.listCarAllocs({ q: w || undefined, limit: 60, scope: 'active', onDate, started: true }).then((r) => r.rows)));
      const seen = new Set<number>();
      setHits(lists.flat().filter((a) => !seen.has(a.car_alloc_id) && !!seen.add(a.car_alloc_id)));
    } finally {
      setSearching(false);
    }
  }

  function search(e: React.FormEvent) {
    e.preventDefault();
    void run(term);
  }

  // 치는 대로 찾는다 — 예전엔 `찾기` 를 눌러야만 돌아서 "부분검색이 안 된다"로 읽혔다.
  useAutoSearch(term, (v) => { void run(v); });
  // 열자마자 정산 진행중 편성 20건을 보여준다 — 빈 칸 앞에서 무엇을 칠지 모르는 것보다 낫다.
  useEffect(() => { void run(''); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="rounded-lg border border-line bg-canvas p-3">
      <form onSubmit={search} className="flex gap-2">
        <input
          className="field flex-1"
          placeholder="차량번호 · 기사명 · 차주명"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
        />
        <button className="btn-ghost" type="submit" disabled={searching}>
          찾기
        </button>
        <button className="btn-ghost" type="button" onClick={onCancel}>
          닫기
        </button>
      </form>
      {onDate && (
        <p className="mt-1 text-xs text-ink-soft">출발일 {onDate} 에 운행하는 편성만 보입니다(시작 전·끝난 편성은 배정할 수 없습니다).</p>
      )}
      {picked.size > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-1.5 rounded-lg border border-brand-200 bg-brand-50 px-2 py-1.5 text-xs">
          <span className="font-semibold text-brand-800">고른 {picked.size}명</span>
          {[...picked.values()].map((a) => (
            <span key={a.car_alloc_id} className="inline-flex items-center gap-1 rounded-full bg-surface px-2 py-0.5 ring-1 ring-line">
              {dash(a.car_no)} {dash(a.driver_name)}
              <button type="button" className="text-ink-faint hover:text-ink" onClick={() => toggle(a)} aria-label="빼기">×</button>
            </span>
          ))}
          <span className="ml-auto flex gap-1.5">
            <button type="button" className="btn-ghost py-0.5 text-xs" disabled={busy} onClick={() => setPicked(new Map())}>비우기</button>
            <button type="button" className="btn-primary py-0.5 text-xs" disabled={busy} onClick={() => onPickMany([...picked.values()])}>
              고른 {picked.size}명 배정
            </button>
          </span>
        </div>
      )}
      {groups.length > 0 && (
        <ul className="mt-2 max-h-64 overflow-y-auto rounded-lg border border-line bg-surface">
          {/* #356 — 기사님 한 분 = 한 줄. 편성이 여럿(차량을 바꿔 타는 기사)이면 가장 최근 편성을 앞에 두고
              나머지는 「다른 편성 N개」로 접어 둔다(고를 수는 있게 — 그날 탈 차량이 다를 수 있다). */}
          {groups.map((g) => {
            const [first, ...rest] = g.list;
            const open = unfolded.has(g.key);
            return (
              <li key={g.key} className="border-b border-line last:border-b-0">
                <div className="flex items-center">
                  <PickBox a={first!} picked={picked} onToggle={toggle} />
                  <AllocLine a={first!} busy={busy} onPick={onPick} />
                  {rest.length > 0 && (
                    <button type="button" className="shrink-0 px-3 py-2 text-xs text-brand-700 hover:underline"
                            onClick={() => setUnfolded((s) => { const n = new Set(s); if (n.has(g.key)) n.delete(g.key); else n.add(g.key); return n; })}>
                      다른 편성 {rest.length}개 {open ? '▴' : '▾'}
                    </button>
                  )}
                </div>
                {open && rest.map((a) => (
                  <div key={a.car_alloc_id} className="flex items-center bg-canvas pl-4">
                    <PickBox a={a} picked={picked} onToggle={toggle} />
                    <AllocLine a={a} busy={busy} onPick={onPick} />
                  </div>
                ))}
              </li>
            );
          })}
        </ul>
      )}
      {/* ⚠️ 이 안내는 **늘** 보여야 한다. 예전엔 결과가 0건일 때만 떠서, 아직 검색이 안 돈 것과
          "찾았는데 없다"가 같은 화면으로 보였다 — 치는 대로 찾게 바꾼 지금은 둘을 갈라 적는다. */}
      <p className="mt-2 text-xs text-ink-faint">
        {searching
          ? '찾는 중…'
          : hits.length === 0 && term.trim() !== ''
            ? `'${term.trim()}' 에 해당하는 편성이 없습니다 — 정산 진행중인 편성만 검색됩니다.`
            : '차량번호 · 기사 · 차주 · 소속 일부만 쳐도 찾습니다. 여러 명은 띄어 쓰거나 쉼표로 나열(예: 김갑식 김영수). 줄을 누르면 바로 배정 · 네모로 여러 명 고른 뒤 「고른 N명 배정」.'}
      </p>
    </div>
  );
}

/** #373 여러 명 고르기 네모. */
function PickBox({ a, picked, onToggle }: { a: CarAlloc; picked: Map<number, CarAlloc>; onToggle: (a: CarAlloc) => void }) {
  return (
    <label className="flex shrink-0 cursor-pointer items-center self-stretch pl-3 pr-1" title="여러 명 고르기">
      <input type="checkbox" checked={picked.has(a.car_alloc_id)} onChange={() => onToggle(a)} />
    </label>
  );
}

/** 배정 후보 한 줄 — 차량 · 기사 · 소속·차주 · 편성 NO·기간(#200). */
function AllocLine({ a, busy, onPick }: { a: CarAlloc; busy: boolean; onPick: (a: CarAlloc) => void }) {
  return (
    <button
      type="button"
      disabled={busy}
      className="min-w-0 flex-1 px-3 py-2 text-left text-sm hover:bg-canvas disabled:opacity-50"
      onClick={() => onPick(a)}
    >
      <b>{dash(a.car_no)}</b>
      <span className="ml-2">{dash(a.driver_name)}</span>
      <span className="ml-2 text-xs text-ink-faint">
        {dash(a.grp_name)} · {dash(a.owner_name)}
      </span>
      <span className="ml-2 text-xs text-ink-soft">
        편성 NO {a.car_alloc_id}
        {(a.startdt || a.enddt) && ` · ${a.startdt || '…'} ~ ${a.enddt || ''}`}
      </span>
    </button>
  );
}

/** 순서 무관 같은 숫자 모음인가. */
function sameSet(a: number[], b: number[]): boolean {
  if (a.length !== b.length) return false;
  const s = new Set(a);
  return b.every((x) => s.has(x));
}

/** #390 노선 상세 위 입력 팝업 — 무엇을 어느 노선에 넣는지 머리 한 줄 + 오류는 팝업 안에(뒤에 가리지 않게). */
function InputPopup({ title, sub, error, children }: { title: string; sub?: string; error?: string | null; children: React.ReactNode }) {
  return (
    <div className="card space-y-3 p-5">
      <div className="flex items-baseline gap-2 border-b border-line pb-2">
        <h3 className="text-base font-bold">{title}</h3>
        {sub && <span className="truncate text-sm text-ink-soft">{sub}</span>}
      </div>
      {error && <ErrorBox message={error} />}
      {children}
    </div>
  );
}
